/**
 * Reports Page
 * 
 * Generate and export cross-business reports
 */

import React from 'react'
import { Card, Row, Col, Typography, Button, Space, DatePicker, Select, Alert } from 'antd'
import { FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons'
import { useGetDashboardQuery } from '../services/corporate'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

const Reports: React.FC = () => {
  const { data, isLoading } = useGetDashboardQuery()
  const [dateRange, setDateRange] = React.useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs(),
  ])
  const [reportType, setReportType] = React.useState<string>('summary')

  const handleExportCSV = () => {
    // In a real implementation, this would call an API endpoint
    console.log('Exporting CSV report...', { dateRange, reportType })
    alert('CSV export functionality would be implemented here')
  }

  const handleExportPDF = () => {
    // In a real implementation, this would call an API endpoint
    console.log('Exporting PDF report...', { dateRange, reportType })
    alert('PDF export functionality would be implemented here')
  }

  return (
    <div>
      <Title level={2}>Reports & Analytics</Title>

      {/* Report Configuration */}
      <Card title="Report Configuration" style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Text strong>Report Type:</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={reportType}
                onChange={setReportType}
              >
                <Option value="summary">Executive Summary</Option>
                <Option value="revenue">Revenue Analysis</Option>
                <Option value="jobs">Job Performance</Option>
                <Option value="customers">Customer Analytics</Option>
                <Option value="comparison">Business Comparison</Option>
              </Select>
            </Col>
            <Col xs={24} md={12}>
              <Text strong>Date Range:</Text>
              <RangePicker
                style={{ width: '100%', marginTop: 8 }}
                value={dateRange}
                onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col>
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleExportCSV}
                loading={isLoading}
              >
                Export to CSV
              </Button>
            </Col>
            <Col>
              <Button
                icon={<FilePdfOutlined />}
                onClick={handleExportPDF}
                loading={isLoading}
              >
                Export to PDF
              </Button>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* Report Preview */}
      <Card title="Report Preview" bordered={false}>
        {data ? (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Title level={4}>Executive Summary</Title>
              <Text type="secondary">
                Period: {dateRange[0].format('MMM DD, YYYY')} - {dateRange[1].format('MMM DD, YYYY')}
              </Text>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Total Revenue"
                    value={data.summary.totalRevenue}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Total Jobs"
                    value={data.summary.totalJobs}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Total Customers"
                    value={data.summary.totalCustomers}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Businesses"
                    value={data.summary.businessCount}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
            </Row>

            <Alert
              message="Report Generation"
              description="This is a preview of the report. Use the export buttons above to download the full report in your preferred format."
              type="info"
              showIcon
            />
          </Space>
        ) : (
          <Alert
            message="No Data Available"
            description="No data available for the selected date range."
            type="warning"
            showIcon
          />
        )}
      </Card>
    </div>
  )
}

// Add Statistic component import at the top
import { Statistic } from 'antd'

export default Reports

